import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Banknote, QrCode, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
} from '@mlm/ui';
import {
  cancelPayoutRequest,
  createPayoutRequest,
  getCashoutWindow,
  getMe,
  getSlotWallet,
  listMyPayouts,
  listPayoutMethods,
  listPayoutQrs,
  previewPayout,
  type PayoutMethodField,
  type PayoutPreview,
  type PayoutRequest,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { fmtCurrency, fmtDateTime } from '../lib/format';
import { WalletPanel } from '../components/wallet/wallet-panel';

const AMOUNT_RE = /^\d+(\.\d{1,4})?$/;

// Friendly copy for the few request errors a member can actually hit; anything
// else falls back to the humanized error code.
const PAYOUT_ERROR_MESSAGES: Record<string, string> = {
  slot_in_debt:
    'This slot still has an open CD debt. Its cash earnings pay the debt off first — you can cash out once it settles.',
  cashout_account_taken:
    'This account is already registered to another member. Use a cash-out account that belongs to you.',
  payout_qr_required:
    'A payout QR code is required for this method. Save one under Settings → Payout QR, then try again.',
};

export function CashOutPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { activeSlotLabel, activeSlotId } = useMySlots();

  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: !!activeSlotId,
  });

  const payoutsQ = useQuery({
    queryKey: ['payouts', activeSlotId],
    queryFn: () => listMyPayouts(activeSlotId ?? undefined),
    enabled: !!activeSlotId,
  });

  // Payout methods are admin-managed — fetched, not hardcoded.
  const methodsQ = useQuery({ queryKey: ['payout-methods'], queryFn: listPayoutMethods });
  const methods = methodsQ.data ?? [];

  // A member blocked from cashing out an in-debt CD slot can pay it off by
  // topping up (when both the top-up feature and the settle-by-top-up policy are on).
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const topupPayoffHref =
    meQ.data?.tenantPolicy?.walletTopupEnabled && meQ.data?.tenantPolicy?.cdTopupSettlesDebt
      ? '/wallet-topup'
      : null;

  // Cash-out window (admin schedule). The server also enforces it on submit.
  const windowQ = useQuery({ queryKey: ['cashout-window'], queryFn: getCashoutWindow });
  const cashoutOpen = windowQ.data?.open ?? true;

  // Min/max amount bounds (server-enforced; mirrored here for a hint + early
  // disable). Effective single-request min respects the per-member min too.
  const limits = windowQ.data?.limits;
  const effMin = Math.max(limits?.perSlotMin ?? 0, limits?.perMemberMin ?? 0);
  const effMax = limits?.perSlotMax ?? null;

  // KYC gate mirrors the tenant flag: only block when the tenant requires KYC
  // and the member isn't verified. Default to requiring it until the window
  // query resolves (matches the server default).
  const requireKyc = windowQ.data?.requireKyc ?? true;
  const kycOk = !requireKyc || user?.kycStatus === 'verified';

  // Multiple-cashout gate (server-enforced; mirrored for an early disable +
  // hint). `payoutsQ` is scoped to the active slot, which exactly covers the
  // 'slot' scope and the common case of 'member' scope; for a pending request
  // on a DIFFERENT slot under 'member' scope the server still rejects.
  // Payout-QR gate mirrors the tenant flag. The same query key as
  // `PayoutQrNotice` below, so TanStack dedupes it to one request; we need the
  // answer up here to disable submit. Default to NOT required until the window
  // query resolves (matches the server default).
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });
  const qrRequired = windowQ.data?.payoutQrRequired === true;
  const hasQrForMethod = (qrsQ.data ?? []).some((q) => q.methodCode === method);
  const qrOk = !qrRequired || hasQrForMethod;

  const multipleAllowed = windowQ.data?.multipleCashoutRequestsAllowed ?? true;
  const cancellationAllowed = windowQ.data?.cancellationAllowed ?? true;
  const hasPendingRequest = (payoutsQ.data ?? []).some((p) =>
    ['requested', 'queued', 'processing'].includes(p.status),
  );
  const blockedByPending = !multipleAllowed && hasPendingRequest;

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  // Account inputs, keyed by field key (or `account` for the single-field default).
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Default the selection to the first method once they load (or repair it if
  // the current pick is no longer offered).
  useEffect(() => {
    if (methods.length === 0) return;
    if (!methods.some((m) => m.code === method)) setMethod(methods[0]!.code);
  }, [methods, method]);

  const selectedMethod = methods.find((m) => m.code === method);
  const noMethods = methodsQ.isSuccess && methods.length === 0;

  // The inputs to render: admin-defined `fields`, or the single legacy account
  // field (stored under `account`) when the method defines none.
  const accountFields: PayoutMethodField[] =
    selectedMethod && selectedMethod.fields.length > 0
      ? selectedMethod.fields
      : [
          {
            key: 'account',
            label: selectedMethod?.accountLabel ?? 'Account details',
            placeholder: selectedMethod?.accountPlaceholder ?? '',
            required: true,
          },
        ];

  // Cash-out security: when the tenant locks the holder name to the owner, the
  // account-holder field is forced to the member's own full name (the server
  // re-applies this authoritatively — this just makes the form reflect it).
  const lockName = windowQ.data?.lockCashoutNameToOwner === true;
  const ownerFullName = meQ.data?.fullName ?? '';

  // Reset entered values when the method changes (keys differ per method).
  useEffect(() => {
    setFieldValues({});
  }, [method]);

  // Seed the locked holder-name field with the owner's full name.
  useEffect(() => {
    if (!lockName || !ownerFullName) return;
    const holder = accountFields.find((f) => f.role === 'account_holder');
    if (holder) setFieldValues((v) => ({ ...v, [holder.key]: ownerFullName }));
    // Re-seed when the method changes (fields differ) or the name/flag resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockName, ownerFullName, method]);

  const accountComplete = accountFields.every(
    (f) => f.required === false || (fieldValues[f.key]?.trim() ?? '') !== '',
  );
  const payload: Record<string, string> = {};
  for (const f of accountFields) {
    const v = fieldValues[f.key]?.trim();
    if (v) payload[f.key] = v;
  }

  // Debounce the amount so the live breakdown preview doesn't fire on every
  // keystroke. The math is server-authoritative (per-method brackets), so we
  // ask the API rather than recomputing in the client.
  const [debouncedAmount, setDebouncedAmount] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAmount(amount), 350);
    return () => clearTimeout(t);
  }, [amount]);

  const amountValid = AMOUNT_RE.test(debouncedAmount) && Number(debouncedAmount) > 0;
  const amountNum = Number(amount);
  const amountOutOfBounds =
    !!amount &&
    ((effMin > 0 && amountNum < effMin) || (effMax != null && amountNum > effMax));
  const previewQ = useQuery({
    queryKey: ['payout-preview', activeSlotId, debouncedAmount, method],
    queryFn: () => previewPayout({ slotId: activeSlotId!, amount: debouncedAmount, method }),
    enabled: !!activeSlotId && amountValid && !!method,
  });
  const preview = previewQ.data;
  const netNonPositive = !!preview && !preview.netPositive;

  const createMut = useMutation({
    meta: { successMessage: 'Payout requested.' },
    mutationFn: () =>
      createPayoutRequest({
        slotId: activeSlotId!,
        amount,
        method,
        payload,
      }),
    onSuccess: () => {
      setAmount('');
      setFieldValues({});
      setError(null);
      qc.invalidateQueries({ queryKey: ['payouts', activeSlotId] });
      qc.invalidateQueries({ queryKey: ['wallet', activeSlotId] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error ?? 'request_failed');
    },
  });

  const cancelMut = useMutation({
    meta: { successMessage: 'Payout request cancelled.' },
    mutationFn: cancelPayoutRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts', activeSlotId] });
      qc.invalidateQueries({ queryKey: ['wallet', activeSlotId] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Banknote className="h-6 w-6 text-muted-foreground" /> Cash Out
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Request a payout from this slot's wallet:
          <span className="ml-1 font-medium text-foreground">{activeSlotLabel}</span>
        </p>
      </div>

      {walletQ.data ? (
        <WalletPanel
          wallet={walletQ.data}
          slotLabel={activeSlotLabel}
          hideCashOut
          topupPayoffHref={topupPayoffHref}
        />
      ) : (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!kycOk && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              Your KYC isn't verified yet. Complete KYC in Settings to request payouts.
            </div>
          )}
          {noMethods && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              No payout methods are available yet. Please contact your administrator.
            </div>
          )}
          {!cashoutOpen && windowQ.data && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              Cash-out is currently closed. It's open{' '}
              {windowQ.data.schedule.openHour}:00–{windowQ.data.schedule.closeHour}:00
              {' '}({windowQ.data.timezone}) on selected days.
            </div>
          )}
          {blockedByPending && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              You already have a pending cash-out request. Wait until it's paid
              or cancelled before requesting another.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Amount</label>
              <Input
                type="number"
                value={amount}
                step="0.01"
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              {(effMin > 0 || effMax != null) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {effMin > 0 && `Min ${fmtCurrency(effMin)}`}
                  {effMin > 0 && effMax != null && ' · '}
                  {effMax != null && `Max ${fmtCurrency(effMax)} per request`}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                disabled={noMethods}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {methods.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            {accountFields.map((f) => {
              const locked = lockName && f.role === 'account_holder';
              return (
                <div key={f.key}>
                  <label className="text-xs text-muted-foreground">
                    {f.label}
                    {f.required === false && (
                      <span className="ml-1 text-muted-foreground/70">(optional)</span>
                    )}
                  </label>
                  <Input
                    value={locked ? ownerFullName : fieldValues[f.key] ?? ''}
                    onChange={(e) =>
                      setFieldValues((v) => ({ ...v, [f.key]: e.target.value }))
                    }
                    placeholder={f.placeholder || ''}
                    readOnly={locked}
                    className={locked ? 'bg-muted' : undefined}
                  />
                  {locked && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Locked to your account name.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <PayoutQrNotice
            methodCode={method}
            methodLabel={selectedMethod?.label ?? method}
            required={qrRequired}
          />
          {amountValid && preview && <PayoutBreakdown preview={preview} />}
          {netNonPositive && (
            <p className="text-xs text-destructive">
              Deductions exceed this amount — nothing would be left to pay out. Request a larger amount.
            </p>
          )}
          {amountOutOfBounds && (
            <p className="text-xs text-destructive">
              Amount must be{effMin > 0 ? ` at least ${fmtCurrency(effMin)}` : ''}
              {effMin > 0 && effMax != null ? ' and' : ''}
              {effMax != null ? ` at most ${fmtCurrency(effMax)}` : ''}.
            </p>
          )}
          {error && (
            <p className="text-xs text-destructive">
              {PAYOUT_ERROR_MESSAGES[error] ?? error.replace(/_/g, ' ')}
            </p>
          )}
          <Button
            onClick={() => createMut.mutate()}
            disabled={
              !kycOk ||
              !qrOk ||
              !amount ||
              !accountComplete ||
              !method ||
              createMut.isPending ||
              !activeSlotId ||
              netNonPositive ||
              amountOutOfBounds ||
              !cashoutOpen ||
              blockedByPending
            }
            className="w-full"
          >
            {createMut.isPending
              ? 'Submitting…'
              : !cashoutOpen
                ? 'Cash-out closed'
                : blockedByPending
                  ? 'Request pending'
                  : !qrOk
                    ? 'QR code required'
                    : 'Request payout'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payoutsQ.isLoading ? (
            <div className="p-6">
              <Skeleton className="h-20" />
            </div>
          ) : !payoutsQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No payout requests yet.</p>
          ) : (
            <ul className="divide-y">
              {payoutsQ.data.map((p) => (
                <PayoutRow
                  key={p.id}
                  p={p}
                  onCancel={() => cancelMut.mutate(p.id)}
                  canceling={cancelMut.isPending}
                  cancellationAllowed={cancellationAllowed}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PayoutRow({
  p,
  onCancel,
  canceling,
  cancellationAllowed,
}: {
  p: PayoutRequest;
  onCancel: () => void;
  canceling: boolean;
  cancellationAllowed: boolean;
}) {
  // Read the snapshot's applied order so history matches the breakdown card the
  // member saw when requesting (these two used to disagree: the card rendered
  // service → admin → tax while this line rendered tax → service → admin).
  const snapshotSteps = p.deductions?.steps;
  const parts: string[] =
    Array.isArray(snapshotSteps) && snapshotSteps.length > 0
      ? snapshotSteps.map((s) => `${s.label} −${fmtCurrency(s.amount, p.currency)}`)
      : [];
  if (parts.length === 0) {
    // Requests issued before `steps` was snapshotted.
    if (Number(p.serviceCharge) > 0) parts.push(`Service −${fmtCurrency(p.serviceCharge, p.currency)}`);
    if (Number(p.adminFee) > 0) parts.push(`Admin −${fmtCurrency(p.adminFee, p.currency)}`);
    if (Number(p.withholdingTax) > 0) parts.push(`WTAX −${fmtCurrency(p.withholdingTax, p.currency)}`);
  }
  const hasDeductions = parts.length > 0;
  return (
    <li className="flex items-center gap-3 px-6 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-mono tabular-nums">
          {fmtCurrency(p.netAmount, p.currency)}
          {hasDeductions && <span className="ml-1 text-xs font-normal text-muted-foreground">net</span>}
        </div>
        <div className="text-xs text-muted-foreground">
          {hasDeductions ? `Gross ${fmtCurrency(p.amount, p.currency)} · ` : ''}
          {p.method.replace('_', ' ')} · {fmtDateTime(p.requestedAt)}
        </div>
        {hasDeductions && (
          <div className="text-xs text-muted-foreground mt-0.5">{parts.join(' · ')}</div>
        )}
        {p.rejectionReason && (
          <div className="text-xs text-destructive mt-1">
            Rejected: {p.rejectionReason}
          </div>
        )}
      </div>
      <Badge variant={badgeFor(p.status)}>{p.status}</Badge>
      {p.status === 'requested' && cancellationAllowed && (
        <Button size="sm" variant="outline" onClick={onCancel} disabled={canceling}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      )}
    </li>
  );
}

/** Live net-payout breakdown shown under the request form. */
function PayoutBreakdown({ preview }: { preview: PayoutPreview }) {
  const c = preview.currency;
  // `steps` is the server's applied sequence — render it verbatim so the member
  // sees the deductions in the order they were actually taken. The fallback
  // covers a preview served by an older API build.
  const rows: Array<{ label: string; value: string }> =
    preview.steps && preview.steps.length > 0
      ? preview.steps.map((s) => ({
          label: s.mode === 'percent' ? `${s.label} (${String(Number(s.value))}%)` : s.label,
          value: `−${fmtCurrency(s.amount, c)}`,
        }))
      : [];
  if (rows.length === 0) {
    if (Number(preview.serviceCharge) > 0)
      rows.push({ label: 'Service charge', value: `−${fmtCurrency(preview.serviceCharge, c)}` });
    if (Number(preview.adminFee) > 0)
      rows.push({ label: 'Admin fee', value: `−${fmtCurrency(preview.adminFee, c)}` });
    if (Number(preview.withholdingTax) > 0)
      rows.push({
        label: `Withholding tax (${String(Number(preview.withholdingTaxRate))}%)`,
        value: `−${fmtCurrency(preview.withholdingTax, c)}`,
      });
  }
  const hasAny = rows.length > 0;
  return (
    <div className="rounded-md bg-muted/40 p-3 space-y-1 text-xs">
      <div className="flex justify-between">
        <span>Gross amount</span>
        <span className="tabular-nums">{fmtCurrency(preview.gross, c)}</span>
      </div>
      {rows.map((r) => (
        <div key={r.label} className="flex justify-between text-muted-foreground">
          <span>{r.label}</span>
          <span className="tabular-nums">{r.value}</span>
        </div>
      ))}
      <div className="border-t pt-1 mt-1 flex justify-between font-semibold">
        <span>{hasAny ? 'Net payout' : 'You receive'}</span>
        <span className="tabular-nums">{fmtCurrency(preview.net, c)}</span>
      </div>
    </div>
  );
}

function badgeFor(
  s: PayoutRequest['status'],
): 'success' | 'warning' | 'destructive' | 'muted' | 'outline' {
  switch (s) {
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'cancelled':
      return 'muted';
    case 'requested':
    case 'queued':
    case 'processing':
      return 'warning';
    default:
      return 'outline';
  }
}

/**
 * The QR this payout will actually be sent to. Read-only here on purpose — the
 * member manages saved QRs in Settings → Payout QR, and whatever is saved for
 * the selected method is SNAPSHOTTED onto the request at submit time, so the
 * finance team reviews the image as it was when the request was made.
 *
 * When the tenant sets `payout_qr_required`, a missing QR is a blocker rather
 * than a note — the parent disables submit and the server rejects regardless.
 */
function PayoutQrNotice({
  methodCode,
  methodLabel,
  required,
}: {
  methodCode: string;
  methodLabel: string;
  required: boolean;
}) {
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });
  if (!methodCode || qrsQ.isLoading) return null;
  const qr = (qrsQ.data ?? []).find((q) => q.methodCode === methodCode);
  const blocking = required && !qr;

  return (
    <div
      className={
        blocking
          ? 'rounded-md border border-warning/30 bg-warning/10 p-3'
          : 'rounded-md border bg-muted/20 p-3'
      }
    >
      <div className="flex items-start gap-3">
        {qr ? (
          <img
            src={qr.qrUrl}
            alt={`${methodLabel} payout QR`}
            className="h-20 w-20 shrink-0 rounded border bg-background object-contain"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded border border-dashed text-muted-foreground">
            <QrCode className="h-6 w-6" />
          </div>
        )}
        <div className="min-w-0 text-xs">
          <p className="font-medium">
            QR code for this payout{blocking ? ' — required' : ''}
          </p>
          {qr ? (
            <p className="mt-0.5 text-muted-foreground">
              {qr.label || `Your saved ${methodLabel} QR`} will be attached to this request.
            </p>
          ) : blocking ? (
            <p className="mt-0.5 text-muted-foreground">
              {methodLabel} payouts need a QR code. Save the QR your payment should be sent to
              before requesting a cash-out.
            </p>
          ) : (
            <p className="mt-0.5 text-muted-foreground">
              You haven&apos;t saved a QR code for {methodLabel} yet. Your payout will be sent
              using the account details above.
            </p>
          )}
          <Link
            to="/settings?tab=payout-qr"
            className="mt-1 inline-block text-primary underline underline-offset-2"
          >
            {qr ? 'Change in Settings' : 'Add one in Settings'}
          </Link>
        </div>
      </div>
    </div>
  );
}
